/**
 * ChartViewButtons — Reusable toolbar buttons for chart view switching
 *
 * Extracted from ChartTabs to eliminate duplication between empty-state
 * and normal tab bar rendering paths.
 */

import React from 'react';
import { Box, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { ChakraTogglesPanel } from './DataSections/components/ChakraTogglesPanel';
import { HoraClockDesignPanel } from './HoraClockDesignPanel';
import type { ChartViewMode } from '../../../types/ChartViewTypes';

const KalaIcon = () => (
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
        <circle cx="15.5564" cy="15.5564" r="10.9" stroke="currentColor" strokeWidth="0.8"/>
        <circle cx="15.5564" cy="15.5564" r="7.9" stroke="currentColor" strokeWidth="0.8"/>
        <path d="M15.5564 7.5564V4.5564M15.5564 23.5564V26.5564M23.5564 15.5564H26.5564M7.5564 15.5564H4.5564" stroke="currentColor" strokeWidth="0.8"/>
        <path d="M21.2133 9.89949L23.3346 7.77817M9.89954 21.2132L7.77822 23.3345M21.2133 21.2132L23.3346 23.3345M9.89954 9.89949L7.77822 7.77817" stroke="currentColor" strokeWidth="0.8"/>
    </svg>
);

const RashiIcon = () => (
    <svg width="16" height="16" viewBox="0 0 22 22" fill="none">
        <rect x="1.1" y="1.1" width="19.8" height="19.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="0.1" y="0.1" width="21.8" height="21.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="1.1" y="16.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="6.1" y="16.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="11.1" y="16.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="16.1" y="16.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="1.1" y="1.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="6.1" y="1.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="11.1" y="1.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="16.1" y="1.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="16.1" y="11.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="16.1" y="6.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="1.1" y="11.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <rect x="1.1" y="6.1" width="4.8" height="4.8" stroke="currentColor" strokeWidth="0.8"/>
        <path d="M6 6V16H16V6" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M6 6H16" stroke="currentColor" strokeWidth="1.2"/>
    </svg>
);

const BUTTON_STYLE = {
    height: '32px',
    padding: '0 8px',
    fontSize: '11px',
    lineHeight: '32px',
    borderRadius: '6px',
    minWidth: '32px',
    border: '1px solid transparent',
    background: 'transparent',
    color: '#718096',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
};

interface ChartViewButtonsProps {
    activeChartId: string;
    activeView: ChartViewMode;
    onSwitchView: (view: ChartViewMode) => void;
    onCopy: () => void;
}

export const ChartViewButtons: React.FC<ChartViewButtonsProps> = ({
    activeChartId,
    activeView,
    onSwitchView,
    onCopy,
}) => (
    <>
        {/* Chakra Toggles Panel */}
        <Box display="flex" alignItems="center" mr={1}>
            <ChakraTogglesPanel chartId={activeChartId} buttonStyle={BUTTON_STYLE} />
        </Box>

        {/* Copy to clipboard */}
        <Tooltip label="Copy chart data" hasArrow>
            <IconButton
                aria-label="Copy chart data"
                icon={<Text fontSize="11px">copy</Text>}
                size="sm"
                variant="ghost"
                onClick={onCopy}
                height="32px"
                minWidth="32px"
                mr={1}
                _hover={{ bg: 'gray.100' }}
                color="gray.600"
            />
        </Tooltip>

        {/* Hora Clock toggle */}
        <Tooltip label="Hora Clock" hasArrow>
            <button
                className={`chart-view-btn${activeView === 'horaClock' ? ' active' : ''}`}
                onClick={() => onSwitchView('horaClock')}
                aria-label="Hora Clock"
            >
                <KalaIcon />
            </button>
        </Tooltip>

        {/* Hora Clock Design Panel — only when hora clock is active */}
        {activeView === 'horaClock' && (
            <HoraClockDesignPanel chartId={activeChartId} />
        )}

        {/* Rashi Chakra toggle */}
        <Tooltip label="Rashi Chakra" hasArrow>
            <button
                className={`chart-view-btn${activeView === 'rashiChakra' ? ' active' : ''}`}
                onClick={() => onSwitchView('rashiChakra')}
                aria-label="Rashi Chakra"
            >
                <RashiIcon />
            </button>
        </Tooltip>
    </>
);
